// WP-1.8 (M11 RP-1..4): receivable/payable queries/mutations over the engine-backed API.
import type { RpKind } from '@pfos/engine';
import {
  rpListResponseSchema,
  rpResponseSchema,
  type RpCreateRequest,
  type RpResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';
import { ACCOUNTS_KEY } from './accounts';
import { TRANSACTIONS_KEY } from './transactions';

export const RPS_KEY = ['rps'] as const;

export function useRps(
  filters: { kind?: RpKind; status?: string } = {},
): UseQueryResult<RpResponse[]> {
  const params = new URLSearchParams();
  if (filters.kind !== undefined) params.set('kind', filters.kind);
  if (filters.status !== undefined) params.set('status', filters.status);
  const qs = params.toString();
  return useQuery({
    queryKey: [...RPS_KEY, filters],
    queryFn: async () => {
      const body = rpListResponseSchema.parse(
        await api.get(`/api/v1/rps${qs === '' ? '' : `?${qs}`}`),
      );
      return body.rps;
    },
  });
}

function useInvalidateRps(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: RPS_KEY }),
      queryClient.invalidateQueries({ queryKey: TRANSACTIONS_KEY }), // settling may create one
      queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
    ]);
  };
}

export function useCreateRp(): UseMutationResult<RpResponse, Error, RpCreateRequest> {
  const invalidate = useInvalidateRps();
  return useMutation({
    mutationFn: async (input) => rpResponseSchema.parse(await api.post('/api/v1/rps', input)),
    onSuccess: invalidate,
  });
}

export function useSettleRp(
  id: string,
): UseMutationResult<RpResponse, Error, { amountMinor: number; transactionId: string }> {
  const invalidate = useInvalidateRps();
  return useMutation({
    mutationFn: async (input) =>
      rpResponseSchema.parse(await api.post(`/api/v1/rps/${id}/settle`, input)),
    onSuccess: invalidate,
  });
}

export function useCloseRp(id: string): UseMutationResult<RpResponse, Error, string> {
  const invalidate = useInvalidateRps();
  return useMutation({
    mutationFn: async (reason: string) =>
      rpResponseSchema.parse(await api.post(`/api/v1/rps/${id}/close`, { reason })),
    onSuccess: invalidate,
  });
}

export function useReopenRp(id: string): UseMutationResult<RpResponse, Error, void> {
  const invalidate = useInvalidateRps();
  return useMutation({
    mutationFn: async () => rpResponseSchema.parse(await api.post(`/api/v1/rps/${id}/reopen`)),
    onSuccess: invalidate,
  });
}

// WP-5.5 (OB-3 Data Health guided fix): sets/changes a due date on an undated receivable.
export function useEditRpDueDate(id: string): UseMutationResult<RpResponse, Error, string> {
  const invalidate = useInvalidateRps();
  return useMutation({
    mutationFn: async (dueDate: string) =>
      rpResponseSchema.parse(await api.patch(`/api/v1/rps/${id}/due-date`, { dueDate })),
    onSuccess: invalidate,
  });
}
